import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { useOffline } from '../../context/OfflineContext';
import { ordersAPI, roomGroupsAPI } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { SUCCESS_MESSAGES } from '../../utils/constants';
import { FiCalendar, FiRefreshCw, FiCheckCircle, FiAlertTriangle, FiClipboard, FiAlertCircle, FiUser, FiLogOut, FiList, FiFolder, FiExternalLink } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CleaningOrderCard from '../../components/orders/CleaningOrderCard';
import IncidentModal from '../../components/incidents/IncidentModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import MyScheduleView from './MyScheduleView';
import CleaningStaffCalendar from '../../components/timeEntry/CleaningStaffCalendar';
import TimeClockButton from '../../components/timeEntry/TimeClockButton';

const CleaningOrdersMobile = () => {
  const { user, logout } = useAuth();
  const { selectedDate, setSelectedDate, addNotification } = useApp();
  const { subscribe } = useWebSocket();
  const { isOnline, addToQueue } = useOffline();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [completingOrders, setCompletingOrders] = useState(new Set());
  const [unmarkingOrders, setUnmarkingOrders] = useState(new Set());
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showScheduleView, setShowScheduleView] = useState(false);
  const [roomGroups, setRoomGroups] = useState([]);

  useEffect(() => {
    loadOrders();
    loadRoomGroups();
  }, [selectedDate, user]);

  const loadRoomGroups = async () => {
    try {
      const groups = await roomGroupsAPI.getAll();
      setRoomGroups(groups);
    } catch (error) {
      console.error('Error loading room groups:', error);
      setRoomGroups([]);
    }
  };

  // WebSocket event handler - Update specific order without full reload
  const handleOrderUpdate = useCallback((orderData) => {

    if (!orderData) return;

    // Check if the order is for the selected date
    // orderData.date comes from backend, selectedDate is already in YYYY-MM-DD format
    const orderDateStr = orderData.date.split('T')[0]; // Get YYYY-MM-DD from ISO string or date
    const selectedDateStr = typeof selectedDate === 'string' ? selectedDate : selectedDate.toISOString().split('T')[0];


    if (orderDateStr !== selectedDateStr) {
      return;
    }

    setOrders(prevOrders => {
      const existingOrderIndex = prevOrders.findIndex(o => o.id === orderData.id);
      const existingOrder = existingOrderIndex >= 0 ? prevOrders[existingOrderIndex] : null;

      // Transform backend fields to frontend format
      const transformedOrder = {
        ...orderData,
        bed_disposition_id: orderData.bed_disposition_id,
        bed_disposition_name: orderData.bed_disposition?.name || orderData.bed_disposition_name,
        cleaning_type_id: orderData.cleaning_type_id,
        cleaning_type_name: orderData.cleaning_type_obj?.name || orderData.cleaning_type_name,
        status: orderData.cleaning_timestamp ? 'completed' : 'pending',
        checkout_status: orderData.checkout_timestamp !== null,
      };

      // Add room or common_space data
      if (orderData.room_id) {
        transformedOrder.room = existingOrder?.room || {
          id: orderData.room_id,
          number: orderData.room_number
        };
      } else if (orderData.common_space_id) {
        transformedOrder.common_space = existingOrder?.common_space || {
          id: orderData.common_space_id,
          name: orderData.common_space_name || orderData.common_space?.name
        };
      }

      // If order exists in current list, update it regardless of staff assignment
      if (existingOrderIndex >= 0) {
        const updatedOrders = [...prevOrders];

        // If order was reassigned to someone else, remove it
        if (orderData.cleaning_staff_id !== user?.id) {
          updatedOrders.splice(existingOrderIndex, 1);
        } else {
          // Update existing order
          updatedOrders[existingOrderIndex] = transformedOrder;
        }

        return updatedOrders;
      }

      // If order doesn't exist but is assigned to this user, add it
      if (orderData.cleaning_staff_id === user?.id) {
        return [...prevOrders, transformedOrder];
      }

      // Otherwise, no change
      return prevOrders;
    });
  }, [user, selectedDate]);

  // WebSocket event handler for order deletion
  const handleOrderDelete = useCallback((orderData) => {

    if (orderData) {
      setOrders(prevOrders => prevOrders.filter(o => o.id !== orderData.id));
    }
  }, []);

  // Subscribe to WebSocket events
  useEffect(() => {
    const unsubscribeCreated = subscribe('order_created', handleOrderUpdate);
    const unsubscribeUpdated = subscribe('order_updated', handleOrderUpdate);
    const unsubscribeCheckoutCompleted = subscribe('checkout_completed', handleOrderUpdate);
    const unsubscribeCheckoutUnmarked = subscribe('checkout_unmarked', handleOrderUpdate);
    const unsubscribeCleaningCompleted = subscribe('cleaning_completed', handleOrderUpdate);
    const unsubscribeCleaningUnmarked = subscribe('cleaning_unmarked', handleOrderUpdate);
    const unsubscribeDeleted = subscribe('order_deleted', handleOrderDelete);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeCheckoutCompleted();
      unsubscribeCheckoutUnmarked();
      unsubscribeCleaningCompleted();
      unsubscribeCleaningUnmarked();
      unsubscribeDeleted();
    };
  }, [subscribe, handleOrderUpdate, handleOrderDelete]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const ordersData = await ordersAPI.getMyOrders(selectedDate);

      // Transform backend fields to frontend fields for consistency
      const transformedOrders = ordersData.map(order => ({
        ...order,
        // Map backend field names to frontend field names
        bed_disposition_id: order.bed_disposition_id,
        bed_disposition_name: order.bed_disposition?.name || order.bed_disposition_name,
        cleaning_type_id: order.cleaning_type_id,
        cleaning_type_name: order.cleaning_type_obj?.name || order.cleaning_type_name,
        // Determine status based on cleaning_timestamp
        status: order.cleaning_timestamp ? 'completed' : 'pending',
        // Map checkout_status based on checkout_timestamp
        checkout_status: order.checkout_timestamp !== null,
      }));

      setOrders(transformedOrders);
    } catch (error) {
      console.error('Error loading orders:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar las órdenes'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadOrders();
    setIsRefreshing(false);

    addNotification({
      type: 'info',
      message: 'Órdenes actualizadas'
    });
  };

  const handleCompleteOrder = async (orderId) => {
    try {
      setCompletingOrders(prev => new Set([...prev, orderId]));

      // Update order status locally first (optimistic update)
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? {
                ...order,
                status: 'completed',
                cleaning_timestamp: new Date().toISOString()
              }
            : order
        )
      );

      if (isOnline) {
        // Si hay conexión, enviar al servidor inmediatamente
        await ordersAPI.complete(orderId);
        addNotification({
          type: 'success',
          message: SUCCESS_MESSAGES.ORDER_COMPLETED
        });
      } else {
        // Si no hay conexión, añadir a la cola de sincronización
        addToQueue('complete_order', { orderId });
        addNotification({
          type: 'info',
          message: 'Orden completada. Se sincronizará cuando haya conexión.'
        });
      }

    } catch (error) {
      console.error('Error completing order:', error);

      // Revertir el cambio local si falla
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? {
                ...order,
                status: 'pending',
                cleaning_timestamp: null
              }
            : order
        )
      );

      addNotification({
        type: 'error',
        message: 'Error al marcar la orden como completada'
      });
    } finally {
      setCompletingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const handleUnmarkOrder = async (orderId) => {
    try {
      setUnmarkingOrders(prev => new Set([...prev, orderId]));

      // Update order status locally first (optimistic update)
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? {
                ...order,
                status: 'pending',
                cleaning_timestamp: null
              }
            : order
        )
      );

      if (isOnline) {
        // Si hay conexión, enviar al servidor inmediatamente
        await ordersAPI.unmark(orderId);
        addNotification({
          type: 'success',
          message: 'Orden desmarcada como pendiente'
        });
      } else {
        // Si no hay conexión, añadir a la cola de sincronización
        addToQueue('unmark_order', { orderId });
        addNotification({
          type: 'info',
          message: 'Orden desmarcada. Se sincronizará cuando haya conexión.'
        });
      }

    } catch (error) {
      console.error('Error unmarking order:', error);

      // Revertir el cambio local si falla
      setOrders(prevOrders =>
        prevOrders.map(order =>
          order.id === orderId
            ? {
                ...order,
                status: 'completed',
                cleaning_timestamp: new Date().toISOString()
              }
            : order
        )
      );

      addNotification({
        type: 'error',
        message: 'Error al desmarcar la orden'
      });
    } finally {
      setUnmarkingOrders(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const getOrderStats = () => {
    const total = orders.length;
    const completed = orders.filter(o => o.status === 'completed').length;
    const pending = orders.filter(o => o.status === 'pending' || o.status === 'in_progress').length;

    return { total, completed, pending };
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleLogout = async () => {
    await logout();
  };

  // Group orders by room groups
  const groupOrdersByRoomGroup = (ordersToGroup) => {
    const groups = [];

    // Sort roomGroups by display_order
    const sortedGroups = [...roomGroups].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    // Add orders for each group
    sortedGroups.forEach(group => {
      const groupOrders = ordersToGroup.filter(order =>
        order.room?.room_group_id === group.id
      );
      if (groupOrders.length > 0) {
        groups.push({
          group,
          orders: groupOrders
        });
      }
    });

    // Add orders without a group (ungrouped rooms and common spaces)
    const ungroupedOrders = ordersToGroup.filter(order =>
      !order.room?.room_group_id
    );
    if (ungroupedOrders.length > 0) {
      groups.push({
        group: null, // null indicates ungrouped
        orders: ungroupedOrders
      });
    }

    return groups;
  };

  // Sort orders within a group
  const sortOrders = (ordersToSort) => {
    return ordersToSort.sort((a, b) => {
      // Priority 1: Checkout completed and cleaning pending (highest priority)
      const aPriority = a.checkout_status && a.status === 'pending' ? 1 : 0;
      const bPriority = b.checkout_status && b.status === 'pending' ? 1 : 0;

      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }

      // Priority 2: Sort rooms first, then common spaces
      const aIsRoom = !!a.room_id;
      const bIsRoom = !!b.room_id;

      if (aIsRoom !== bIsRoom) {
        return aIsRoom ? -1 : 1; // Rooms first
      }

      // Priority 3: Sort by room number or space name
      if (aIsRoom) {
        const roomA = parseInt(a.room?.number) || 0;
        const roomB = parseInt(b.room?.number) || 0;
        return roomA - roomB;
      } else {
        const spaceA = a.common_space?.name || '';
        const spaceB = b.common_space?.name || '';
        return spaceA.localeCompare(spaceB);
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" text="Cargando órdenes..." />
      </div>
    );
  }

  const stats = getOrderStats();

  return (
    <div className="w-full mx-auto bg-gray-50 min-h-screen">
      {/* User Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 shadow-md">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div>
                <p className="text-xs text-blue-100">Bienvenido/a</p>
                <p className="text-sm font-semibold text-white">
                  {user?.name || user?.username}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <TimeClockButton />
              <button
                onClick={() => setShowScheduleView(!showScheduleView)}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                title={showScheduleView ? "Ver órdenes" : "Ver cuadrante y fichajes"}
              >
                {showScheduleView ? (
                  <FiList className="h-6 w-6" />
                ) : (
                  <FiCalendar className="h-6 w-6" />
                )}
              </button>
              <button
                onClick={() => setShowIncidentModal(true)}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                title="Reportar incidencia"
              >
                <FiAlertCircle className="h-6 w-6" />
              </button>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-lg transition-colors"
                title="Cerrar sesión"
              >
                <FiLogOut className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Conditional rendering: Schedule View or Orders View */}
      {showScheduleView ? (
        <CleaningStaffCalendar />
      ) : (
        <>
      {/* Orders Header */}
      <div className="bg-white shadow-sm">
        <div className="px-2 py-3">
          <h1 className="text-xl font-bold text-gray-900 text-center mb-3">
            Órdenes de Limpieza
          </h1>

          {/* Date selector */}
          <div className="flex items-center justify-center space-x-3 mb-3">
            <FiCalendar className="h-5 w-5 text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="form-input text-center"
            />
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="btn btn-secondary p-2"
            >
              <FiRefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-green-50 rounded-lg p-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiCheckCircle className="h-4 w-4 text-green-600" />
                <p className="text-xs text-green-600 font-medium">Hechas</p>
              </div>
              <p className="text-lg font-bold text-green-900">{stats.completed}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-2 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FiAlertTriangle className="h-4 w-4 text-yellow-600" />
                <p className="text-xs text-yellow-600 font-medium">Pendientes</p>
              </div>
              <p className="text-lg font-bold text-yellow-900">{stats.pending}</p>
            </div>
          </div>

        </div>
      </div>

      {/* Orders List */}
      <div className="px-2 py-4 space-y-4">
        {orders.length === 0 ? (
          <div className="text-center py-12">
            <FiClipboard className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No hay órdenes
            </h3>
            <p className="text-gray-500 mb-4">
              No tienes órdenes asignadas para {formatDate(new Date(selectedDate))}
            </p>
            <button
              onClick={handleRefresh}
              className="btn btn-primary"
            >
              Actualizar
            </button>
          </div>
        ) : (
          <>
            {/* Pending orders grouped by room groups */}
            {groupOrdersByRoomGroup(orders.filter(order => order.status !== 'completed')).map(({ group, orders: groupOrders }) => (
              <div key={group ? `group-${group.id}` : 'ungrouped'} className="space-y-3">
                {/* Group header - only show if there are groups defined */}
                {roomGroups.length > 0 && (
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg"
                    style={group?.color ? {
                      backgroundColor: `${group.color}20`,
                      borderLeft: `4px solid ${group.color}`
                    } : {}}
                  >
                    {group?.color ? (
                      <span
                        className="w-4 h-4 rounded-full border border-white shadow-sm flex-shrink-0"
                        style={{ backgroundColor: group.color }}
                      />
                    ) : (
                      <FiFolder className="h-4 w-4 text-blue-600" />
                    )}
                    <h3
                      className="text-sm font-semibold"
                      style={{ color: group?.color || 'rgb(55 65 81)' }}
                    >
                      {group ? group.name : 'Otras habitaciones'}
                    </h3>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={group?.color ? {
                        backgroundColor: `${group.color}30`,
                        color: group.color
                      } : { color: 'rgb(107 114 128)' }}
                    >
                      ({groupOrders.length})
                    </span>
                  </div>
                )}
                {/* Orders in this group */}
                {sortOrders([...groupOrders]).map(order => (
                  <CleaningOrderCard
                    key={order.id}
                    order={order}
                    onComplete={handleCompleteOrder}
                    onUnmark={handleUnmarkOrder}
                    isCompleting={completingOrders.has(order.id)}
                    isUnmarking={unmarkingOrders.has(order.id)}
                  />
                ))}
              </div>
            ))}

            {/* Completed orders grouped by room groups */}
            {orders.filter(order => order.status === 'completed').length > 0 && (
              <>
                <div className="py-4">
                  <h3 className="text-lg font-semibold text-gray-700 text-center">
                    Órdenes Completadas
                  </h3>
                </div>
                {groupOrdersByRoomGroup(orders.filter(order => order.status === 'completed')).map(({ group, orders: groupOrders }) => (
                  <div key={group ? `completed-group-${group.id}` : 'completed-ungrouped'} className="space-y-3">
                    {/* Group header - only show if there are groups defined */}
                    {roomGroups.length > 0 && (
                      <div
                        className="flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={group?.color ? {
                          backgroundColor: `${group.color}15`,
                          borderLeft: `4px solid ${group.color}80`
                        } : {}}
                      >
                        {group?.color ? (
                          <span
                            className="w-4 h-4 rounded-full border border-white shadow-sm flex-shrink-0 opacity-60"
                            style={{ backgroundColor: group.color }}
                          />
                        ) : (
                          <FiFolder className="h-4 w-4 text-green-600" />
                        )}
                        <h3
                          className="text-sm font-semibold"
                          style={{ color: group?.color ? `${group.color}CC` : 'rgb(75 85 99)' }}
                        >
                          {group ? group.name : 'Otras habitaciones'}
                        </h3>
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={group?.color ? {
                            backgroundColor: `${group.color}20`,
                            color: `${group.color}CC`
                          } : { color: 'rgb(107 114 128)' }}
                        >
                          ({groupOrders.length})
                        </span>
                      </div>
                    )}
                    {/* Orders in this group */}
                    {sortOrders([...groupOrders]).map(order => (
                      <CleaningOrderCard
                        key={order.id}
                        order={order}
                        onComplete={handleCompleteOrder}
                        onUnmark={handleUnmarkOrder}
                        isCompleting={completingOrders.has(order.id)}
                        isUnmarking={unmarkingOrders.has(order.id)}
                      />
                    ))}
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </div>

      {/* Footer message */}
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">
          ¡Buen trabajo! Mantén la calidad en cada habitación.
        </p>
      </div>
        </>
      )}

      {/* Incident Modal */}
      <IncidentModal
        isOpen={showIncidentModal}
        onClose={() => setShowIncidentModal(false)}
      />

      {/* Logout Confirmation Dialog */}
      {showLogoutConfirm && (
        <ConfirmDialog
          title="Cerrar sesión"
          message="¿Estás seguro de que deseas cerrar sesión?"
          confirmText="Cerrar sesión"
          confirmStyle="danger"
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </div>
  );
};

export default CleaningOrdersMobile;